import { ChangeEvent, useState } from "react";
import FormHelperText from "@mui/material/FormHelperText";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { joinDurationDraft, splitDurationDraft } from "./settingValue";

const HELPER_TEXT_SX = { mx: 1.75, mt: 0 } as const;
const NUMERIC_INPUT = { inputMode: "numeric" } as const;
const HOURS_SLOT_PROPS = {
  htmlInput: NUMERIC_INPUT,
  input: { endAdornment: <InputAdornment position="end">ชม.</InputAdornment> },
} as const;
const MINUTES_SLOT_PROPS = {
  htmlInput: NUMERIC_INPUT,
  input: { endAdornment: <InputAdornment position="end">นาที</InputAdornment> },
} as const;

interface DurationMinutesInputProps {
  /** The draft in total minutes, as text. */
  value: string;
  error: string | null;
  helperText: string;
  disabled: boolean;
  onChange: (minutesText: string) => void;
}

/** A minutes setting edited as hours + minutes; hands back the total minutes as text. */
export default function DurationMinutesInput({
  value,
  error,
  helperText,
  disabled,
  onChange,
}: Readonly<DurationMinutesInputProps>) {
  // Kept as typed, so "0" hours or a blank side is not rewritten under the cursor.
  const [parts, setParts] = useState(() => splitDurationDraft(value));

  const update = (next: { hours: string; minutes: string }) => {
    setParts(next);
    onChange(joinDurationDraft(next.hours, next.minutes));
  };

  const handleHoursChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    update({ ...parts, hours: event.target.value });

  const handleMinutesChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    update({ ...parts, minutes: event.target.value });

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          fullWidth
          aria-label="ชั่วโมง"
          value={parts.hours}
          onChange={handleHoursChange}
          error={Boolean(error)}
          disabled={disabled}
          slotProps={HOURS_SLOT_PROPS}
        />
        <TextField
          size="small"
          fullWidth
          aria-label="นาที"
          value={parts.minutes}
          onChange={handleMinutesChange}
          error={Boolean(error)}
          disabled={disabled}
          slotProps={MINUTES_SLOT_PROPS}
        />
      </Stack>
      <FormHelperText error={Boolean(error)} sx={HELPER_TEXT_SX}>{error ?? helperText}</FormHelperText>
    </Stack>
  );
}
